import React, { useState } from 'react'
import { omitAll } from 'lodash/fp'
import { InfoIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { formatFilterRule } from '@/entities/column'

import { QuickFiltersBar } from '@/widgets/quick-filters-bar'

import { Header } from '@/shared/components/ui/layout'
import { Separator } from '@/shared/components/ui/separator'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { NumberFormats } from '@/shared/tools/locale'

import { getMoMPeriods } from '../../lib/helpers'
import {
	useDashboardStore,
	useSelectDashboardFilters,
	useSelectDashboardFiltersKeys,
} from '../../lib/store'
import { MultiPeriodsSelect } from '../Periods'
import BusinessMetricsWidget from '../widgets/BusinessMetrics/Widget'
import MetricPerfomanceWidget from '../widgets/MetricPerfomance/Widget'
import MetricToCompareWidget from '../widgets/MetricToCompare/Widget'
import NumericGrossProfitWidget from '../widgets/NumericGrossProfit/Widget'
import NumericRevenueWidget from '../widgets/NumericRevenue/Widget'
import NumericSalesItemsWidget from '../widgets/NumericSalesItems/Widget'
import PriceChangeSegmentedWidget from '../widgets/PriceChangeSegmented/Widget'
import RevenueUnderManagementByEngineWidget from '../widgets/RevenueUnderManagementByEngine/Widget'
import { WidgetWrapper } from '../WidgetWrapper'
import { useFilterFieldsConfig } from './useFilterFiledsConfig'

export default function MainDashboardBusinessSection({
	biRevisionDate,
}: {
	biRevisionDate: string
}) {
	const { t } = useTranslation()

	const [periods, setPeriods] = useState(() => getMoMPeriods(biRevisionDate))
	const { setFilters, filters } = useDashboardStore(
		useSelectDashboardFilters('main_business')
	)
	const { storedKeys, setStoredKeys } = useDashboardStore(
		useSelectDashboardFiltersKeys('main_business')
	)
	const { fieldsConfig, isLoading } = useFilterFieldsConfig({
		// remove fields that unsuported by BI widgets in this dashboard section
		sanitizeFields,
	})

	return (
		<section>
			<Header className='sticky top-0 z-10 items-center justify-between bg-background/50 px-4 py-2 backdrop-blur-md'>
				<div className='flex items-center'>
					<h2 className='mb-0 text-lg font-bold'>
						{t('dashboard.section.business.title')}
					</h2>
					<Tooltip>
						<TooltipTrigger asChild>
							<InfoIcon className='ml-2 size-4' />
						</TooltipTrigger>
						<TooltipContent>
							{t('dashboard.section.business.tooltip')}
						</TooltipContent>
					</Tooltip>
				</div>
				<div className='flex items-center space-x-2'>
					{isLoading ? (
						<div className='flex items-center space-x-2'>
							<Skeleton className='h-6 w-28' />
							<Skeleton className='h-6 w-20' />
							<Skeleton className='h-6 w-24' />
						</div>
					) : (
						<QuickFiltersBar
							irremovableKeys={['optimization_group', 'pos_id']}
							rules={filters}
							storedKeys={storedKeys}
							onSaveStoredKeys={(keys) => setStoredKeys('main_business', keys)}
							onApply={(rules) =>
								setFilters('main_business', rules.map(formatFilterRule))
							}
							fieldsConfig={fieldsConfig}
							limit={7}
						/>
					)}
					<Separator orientation='vertical' className='min-h-5' />
					<MultiPeriodsSelect
						periods={periods}
						onChangePeriods={setPeriods}
						defaultPeriods={periods}
					/>
				</div>
			</Header>
			<div className='grid grid-cols-6 gap-4 p-4'>
				<WidgetWrapper className='min-h-36'>
					<NumericSalesItemsWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='min-h-36'>
					<NumericRevenueWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-2 row-span-2 min-h-72'>
					<MetricPerfomanceWidget
						commonPeriods={periods}
						commonFilters={filters}
						params={{ metric: 'sales_items', format: NumberFormats.Integer }}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-2 row-span-2 min-h-72'>
					<RevenueUnderManagementByEngineWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='min-h-36'>
					<NumericGrossProfitWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-2 row-span-2 row-start-3 min-h-72'>
					<MetricPerfomanceWidget
						commonPeriods={periods}
						commonFilters={filters}
						params={{ metric: 'revenue', format: NumberFormats.Integer }}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-2 row-span-2 row-start-3 min-h-72'>
					<MetricPerfomanceWidget
						commonPeriods={periods}
						commonFilters={filters}
						params={{ metric: 'gross_profit', format: NumberFormats.Integer }}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-2 row-span-2 row-start-5 min-h-72'>
					<MetricToCompareWidget commonFilters={filters} />
				</WidgetWrapper>
				<WidgetWrapper className='col-span-4 row-span-2 row-start-5 min-h-72'>
					<PriceChangeSegmentedWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
				<WidgetWrapper className='col-span-6 row-start-11 min-h-36'>
					<BusinessMetricsWidget
						commonPeriods={periods}
						commonFilters={filters}
					/>
				</WidgetWrapper>
			</div>
		</section>
	)
}
const sanitizeFields = omitAll([
	'optimization_group_id',
	'pos_ids',
	'category_ids',
])
